import { useCallback, useMemo } from 'react';
import { useQueryGetProject, useQueryListProjects } from '../../../../core/queries/projects.queries';
import { KnownCreateHubDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutationCreateHub } from '../../../lockers/hubs.queries';
import { EntityForm, NodeReference } from '../form.types';
import { HubSettingsForm } from './form';
import { HubSettingsFormOutput } from './form-output';
import { HubSettingsFormSchema } from './form-schema';
import { hubSettingsFormUISchema } from './ui-schema';
import { DynamicFormLocationField } from '../shared/dynamic-form-location-field';
import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';

export function useCreateHubForm(
  parentProjectID: string | undefined,
  parentNodeId: string | undefined,
  formData: HubSettingsForm | undefined
): EntityForm | null {
  const parentProject = useQueryGetProject(parentProjectID);
  const allProjects = useQueryListProjects();
  const hubMutation = useMutationCreateHub();

  const defaultValues = useMemo(() => {
    return {
      managedBy: parentProjectID,
      type: KnownCreateHubDtoType.LockersHub,
      location: { latitude: -37.8136, longitude: 144.9631 } // Melbourne
    } as HubSettingsForm;
  }, [parentProjectID]);

  const initialFormOutput = useMemo(() => {
    return new HubSettingsFormOutput({});
  }, []);

  const currentFormOutput = useMemo(() => {
    return new HubSettingsFormOutput({
      ...initialFormOutput,
      ...formData
    });
  }, [formData, initialFormOutput]);

  const projectOptions = useMemo(() => {
    return allProjects.data ?? (parentProject.data ? [parentProject.data] : []);
  }, [allProjects.data, parentProject.data]);

  const formSchema = useMemo(() => {
    return HubSettingsFormSchema.FromFormData(currentFormOutput, projectOptions);
  }, [currentFormOutput, projectOptions]);

  const onSubmit = useCallback(
    async (formOutput: HubSettingsFormOutput) => {
      const result = await hubMutation.mutateAsync({
        dto: HubSettingsFormOutput.ToCreateHubDto(formOutput)
      });
      return {
        entityId: result.id,
        parentNodeId
      };
    },
    [parentNodeId, hubMutation]
  );

  const onSubmissionAction = useCallback(
    (dispatch: SettingsActionDispatcher, result: void | NodeReference) => {
      if (!result) return;
      dispatch({
        type: 'SET_CURRENT_ACTION',
        payload: {
          type: 'HUB_CREATED',
          action: {
            hubId: result.entityId,
            hubName: currentFormOutput.name ?? '',
            managedByProjectId: currentFormOutput.managedBy ?? ''
          }
        }
      });
    },
    [currentFormOutput.name, currentFormOutput.managedBy]
  );

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: parentProject.isLoading || allProjects.isLoading,
    title: 'New Hub',
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: hubSettingsFormUISchema,
    fields: { locationPicker: DynamicFormLocationField },
    getOutput(formData) {
      return new HubSettingsFormOutput(formData as HubSettingsForm);
    },
    onSubmit,
    onSubmissionAction,
    isLocked: false,
    lockedMessage: null
  };
}
